// Minimal-but-valid R12 (AC1009) DXF writer. R12 has no LWPOLYLINE/ELLIPSE/
// MTEXT/SPLINE, so the entity vocabulary is LINE, POLYLINE/VERTEX/SEQEND,
// SOLID, TEXT, and INSERT - everything upstream is already flattened to fit.
// Tables declare the CONTINUOUS linetype, the STANDARD text style, and the
// export layers so viewers list them properly.

export const LAYER_SYMBOLS = 'SYMBOLS';
export const LAYER_LABELS = 'LABELS';
export const LAYER_CONDUCTORS = 'CONDUCTORS';
export const LAYER_FRAME = 'FRAME';

/** Layer 0 inside a block means "inherit the INSERT's layer" — classic floating geometry. */
const LAYER_BLOCK = '0';

/** DXF real formatter: fixed precision, trailing zeros stripped, no negative zero. */
export function formatReal(value) {
  if (!Number.isFinite(value)) return '0';
  const fixed = value.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
  return fixed === '-0' ? '0' : fixed;
}

/** TEXT values must be single-line; collapse any stray line breaks. */
const sanitizeText = (value) => value.replace(/[\r\n]+/g, ' ');

const appendTags = (target, source) => {
  for (const tag of source) target.push(tag);
};

// R12 consumers decode text through an ANSI codepage (declared via $DWGCODEPAGE),
// not UTF-8. We declare ANSI_1252 and encode the file accordingly so Spanish
// labels ("Iluminación") survive; anything outside cp1252 uses AutoCAD's \U+XXXX
// escape instead of silently corrupting.
export const DXF_CODEPAGE = 'ANSI_1252';

/** cp1252 bytes 0x80–0x9F → Unicode code points (the rest match Latin-1). */
const CP1252_EXTRAS = new Map([
  [0x20ac, 0x80], [0x201a, 0x82], [0x0192, 0x83], [0x201e, 0x84], [0x2026, 0x85],
  [0x2020, 0x86], [0x2021, 0x87], [0x02c6, 0x88], [0x2030, 0x89], [0x0160, 0x8a],
  [0x2039, 0x8b], [0x0152, 0x8c], [0x017d, 0x8e], [0x2018, 0x91], [0x2019, 0x92],
  [0x201c, 0x93], [0x201d, 0x94], [0x2022, 0x95], [0x2013, 0x96], [0x2014, 0x97],
  [0x02dc, 0x98], [0x2122, 0x99], [0x0161, 0x9a], [0x203a, 0x9b], [0x0153, 0x9c],
  [0x017e, 0x9e], [0x0178, 0x9f],
]);

/** Encode a DXF document string as windows-1252 bytes, escaping the rest as \U+XXXX. */
export function encodeCp1252(text) {
  const bytes = [];
  for (const ch of text) {
    const cp = ch.codePointAt(0);
    if (cp <= 0x7f || (cp >= 0xa0 && cp <= 0xff)) {
      bytes.push(cp);
    } else if (CP1252_EXTRAS.has(cp)) {
      bytes.push(CP1252_EXTRAS.get(cp));
    } else {
      const esc = `\\U+${cp.toString(16).toUpperCase().padStart(4, '0')}`;
      for (let i = 0; i < esc.length; i++) bytes.push(esc.charCodeAt(i));
    }
  }
  return new Uint8Array(bytes);
}

/** Accumulates (group, value) tag pairs for one entity stream. */
export class EntityWriter {
  tags = [];

  tag(code, value) {
    this.tags.push(String(code), typeof value === 'number' ? formatReal(value) : value);
  }

  line(from, to, layer, color = null) {
    this.tag(0, 'LINE');
    this.tag(8, layer);
    if (color && Number.isInteger(color.aci)) this.tag(62, color.aci);
    this.tag(10, from.x);
    this.tag(20, from.y);
    this.tag(30, 0);
    this.tag(11, to.x);
    this.tag(21, to.y);
    this.tag(31, 0);
  }

  polyline(points, closed, layer, color = null) {
    if (points.length < 2) return;
    this.tag(0, 'POLYLINE');
    this.tag(8, layer);
    if (color && Number.isInteger(color.aci)) this.tag(62, color.aci);
    this.tag(66, '1');
    this.tag(70, closed ? '1' : '0');
    for (const p of points) {
      this.tag(0, 'VERTEX');
      this.tag(8, layer);
      this.tag(10, p.x);
      this.tag(20, p.y);
      this.tag(30, 0);
    }
    this.tag(0, 'SEQEND');
    this.tag(8, layer);
  }

  /** Filled quadrilateral. Corners in perimeter order (SOLID's zigzag order is handled here). */
  solid(corners, layer, color = null) {
    const [a, b, c, d] = corners;
    this.tag(0, 'SOLID');
    this.tag(8, layer);
    if (color && Number.isInteger(color.aci)) this.tag(62, color.aci);
    this.tag(10, a.x);
    this.tag(20, a.y);
    this.tag(30, 0);
    this.tag(11, b.x);
    this.tag(21, b.y);
    this.tag(31, 0);
    // SOLID draws 1-2-3 / 3-2-4: swap the last two perimeter corners.
    this.tag(12, d.x);
    this.tag(22, d.y);
    this.tag(32, 0);
    this.tag(13, c.x);
    this.tag(23, c.y);
    this.tag(33, 0);
  }

  text(opts) {
    const content = sanitizeText(opts.content);
    if (!content) return;
    const hAlign = opts.hAlign ?? 0;
    const vAlign = opts.vAlign ?? 0;
    this.tag(0, 'TEXT');
    this.tag(8, opts.layer);
    this.tag(10, opts.x);
    this.tag(20, opts.y);
    this.tag(30, 0);
    this.tag(40, opts.height);
    this.tag(1, content);
    if (opts.rotationDeg) this.tag(50, opts.rotationDeg);
    this.tag(7, 'STANDARD');
    if (hAlign !== 0 || vAlign !== 0) {
      this.tag(72, String(hAlign));
      // With any non-default justification the alignment point (11/21) governs.
      this.tag(11, opts.x);
      this.tag(21, opts.y);
      this.tag(31, 0);
      this.tag(73, String(vAlign));
    }
  }

  insert(blockName, x, y, scale, rotationDeg, layer) {
    this.tag(0, 'INSERT');
    this.tag(8, layer);
    this.tag(2, blockName);
    this.tag(10, x);
    this.tag(20, y);
    this.tag(30, 0);
    this.tag(41, scale);
    this.tag(42, scale);
    this.tag(43, scale);
    this.tag(50, rotationDeg);
  }
}

/** R12 symbol-table names: ≤31 chars from [A-Z0-9$_-]. */
export function sanitizeBlockName(raw) {
  const cleaned = raw
    .replace(/\.[a-zA-Z0-9]+$/, '')
    .replace(/[^A-Za-z0-9_$-]+/g, '_')
    .replace(/_{2,}/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();
  return (cleaned || 'SYMBOL').slice(0, 28);
}

export class DxfDocument {
  entities = new EntityWriter();
  blocks = [];
  usedNames = new Set();

  constructor(pageWidth, pageHeight) {
    this.pageWidth = pageWidth;
    this.pageHeight = pageHeight;
  }

  /** Register a block under a unique sanitized name and return that name. */
  addBlock(rawName, build) {
    const base = sanitizeBlockName(rawName);
    let name = base;
    for (let i = 2; this.usedNames.has(name); i++) name = `${base}_${i}`;
    this.usedNames.add(name);
    const writer = new EntityWriter();
    build(writer);
    this.blocks.push({ name, writer });
    return name;
  }

  blockEntityLayer() {
    return LAYER_BLOCK;
  }

  toString() {
    const tags = [];
    const tag = (code, value) => {
      tags.push(String(code), typeof value === 'number' ? formatReal(value) : value);
    };

    // HEADER
    tag(0, 'SECTION');
    tag(2, 'HEADER');
    tag(9, '$ACADVER');
    tag(1, 'AC1009');
    tag(9, '$DWGCODEPAGE');
    tag(3, DXF_CODEPAGE);
    tag(9, '$EXTMIN');
    tag(10, 0);
    tag(20, 0);
    tag(30, 0);
    tag(9, '$EXTMAX');
    tag(10, this.pageWidth);
    tag(20, this.pageHeight);
    tag(30, 0);
    tag(0, 'ENDSEC');

    // TABLES: LTYPE, LAYER, STYLE
    tag(0, 'SECTION');
    tag(2, 'TABLES');

    tag(0, 'TABLE');
    tag(2, 'LTYPE');
    tag(70, '1');
    tag(0, 'LTYPE');
    tag(2, 'CONTINUOUS');
    tag(70, '0');
    tag(3, 'Solid line');
    tag(72, '65');
    tag(73, '0');
    tag(40, 0);
    tag(0, 'ENDTAB');

    tag(0, 'TABLE');
    tag(2, 'LAYER');
    tag(70, '5');
    for (const layer of ['0', LAYER_SYMBOLS, LAYER_LABELS, LAYER_CONDUCTORS, LAYER_FRAME]) {
      tag(0, 'LAYER');
      tag(2, layer);
      tag(70, '0');
      tag(62, '7');
      tag(6, 'CONTINUOUS');
    }
    tag(0, 'ENDTAB');

    tag(0, 'TABLE');
    tag(2, 'STYLE');
    tag(70, '1');
    tag(0, 'STYLE');
    tag(2, 'STANDARD');
    tag(70, '0');
    tag(40, 0);
    tag(41, 1);
    tag(50, 0);
    tag(71, '0');
    tag(42, 2.5);
    tag(3, 'txt');
    tag(4, '');
    tag(0, 'ENDTAB');

    tag(0, 'ENDSEC');

    // BLOCKS
    tag(0, 'SECTION');
    tag(2, 'BLOCKS');
    for (const block of this.blocks) {
      tag(0, 'BLOCK');
      tag(8, '0');
      tag(2, block.name);
      tag(70, '0');
      tag(10, 0);
      tag(20, 0);
      tag(30, 0);
      tag(3, block.name);
      appendTags(tags, block.writer.tags);
      tag(0, 'ENDBLK');
      tag(8, '0');
    }
    tag(0, 'ENDSEC');

    // ENTITIES
    tag(0, 'SECTION');
    tag(2, 'ENTITIES');
    appendTags(tags, this.entities.tags);
    tag(0, 'ENDSEC');

    tag(0, 'EOF');
    return tags.join('\n');
  }
}
